import React, { useState, useRef, useEffect } from 'react';
import './DiagnosticPopup.css';

// 🌈 COULEURS INDIVIDUELLES POUR CHAQUE FRÉQUENCE THÉRAPEUTIQUE - Identiques au tore
const THERAPY_COLORS = [];
for (let i = 0; i < 42; i++) {
  const hue = (i / 42) * 360; // Distribution uniforme sur tout le spectre
  const saturation = 1.0; // Saturation maximale
  const lightness = 0.6; // Luminosité optimale pour visibilité
  
  // Convertir HSL en RGB puis en hex
  const h = hue / 360;
  const s = saturation;
  const l = lightness;
  
  const hue2rgb = (p, q, t) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };
  
  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;
  const r = hue2rgb(p, q, h + 1/3);
  const g = hue2rgb(p, q, h);
  const b = hue2rgb(p, q, h - 1/3);
  
  const hexColor = ((1 << 24) + (Math.round(r * 255) << 16) + (Math.round(g * 255) << 8) + Math.round(b * 255)).toString(16).slice(1);
  THERAPY_COLORS.push(`#${hexColor}`);
}

const TherapyPopup = ({ data, position, onClose }) => {
  if (!data) return null;

  // États pour la lecture audio
  const [isPlaying, setIsPlaying] = useState(false);
  const oscillatorRef = useRef(null);
  const audioContextRef = useRef(null);

  // Nettoyer l'audio lors de la fermeture du composant
  useEffect(() => {
    return () => {
      stopFrequency();
    };
  }, []);

  // Fonction pour jouer une fréquence thérapeutique
  const playFrequency = async (frequency) => {
    try {
      // Créer ou réutiliser le contexte audio
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      // Arrêter la lecture précédente si elle existe
      if (oscillatorRef.current) {
        oscillatorRef.current.stop();
        oscillatorRef.current = null;
      }

      // Créer un nouvel oscillateur
      oscillatorRef.current = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();

      // Configuration de l'oscillateur
      oscillatorRef.current.type = 'sine'; // Onde sinusoïdale pure pour la thérapie
      oscillatorRef.current.frequency.setValueAtTime(frequency, audioContextRef.current.currentTime);
      
      // Configuration du volume (faible pour la thérapie)
      gainNode.gain.setValueAtTime(0.1, audioContextRef.current.currentTime);

      // Connexion audio
      oscillatorRef.current.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);

      // Démarrer la lecture
      oscillatorRef.current.start();
      setIsPlaying(true);

      // Arrêter automatiquement après 10 secondes
      oscillatorRef.current.onended = () => {
        setIsPlaying(false);
        oscillatorRef.current = null;
      };

      // Programmer l'arrêt automatique
      setTimeout(() => {
        if (oscillatorRef.current) {
          stopFrequency();
        }
      }, 10000); // 10 secondes

    } catch (error) {
      console.error('Erreur lors de la lecture de la fréquence:', error);
      setIsPlaying(false);
    }
  };

  // Fonction pour arrêter la lecture
  const stopFrequency = () => {
    if (oscillatorRef.current) {
      try {
        oscillatorRef.current.stop();
      } catch (error) {
        console.warn('Erreur lors de l\'arrêt de l\'oscillateur:', error);
      }
      oscillatorRef.current = null;
    }
    setIsPlaying(false);
  };

  // Gérer le clic sur play/pause
  const handlePlayPause = () => {
    if (isPlaying) {
      stopFrequency();
    } else {
      // Use data.frequency directly (passed from parent) with THERAPEUTIC_FREQUENCIES as fallback
      const freq = data.frequency || (THERAPEUTIC_FREQUENCIES[data.index] || THERAPEUTIC_FREQUENCIES[0]).frequency;
      playFrequency(freq);
    }
  };

  // Positionner la popup fixe à droite de l'écran avec contraintes
  const popupStyle = {
    position: 'fixed',
    top: '50%',
    right: '20px',
    transform: 'translateY(-50%)',
    maxHeight: '70vh',
    overflowY: 'auto',
    width: '320px'
  };

  // 🎵 LES 42 FRÉQUENCES THÉRAPEUTIQUES THOR 42 SCIENTIFIQUEMENT VALIDÉES
  // Basées sur la thèse THOR 42 : invariant θ₀ = 42π = 131.946891450771 Hz, nombre d'or φ = 1.618033988749
  const THERAPEUTIC_FREQUENCIES = {
    // 🔴 GROUPE 1 - HARMONIQUES θ₀ (7 fréquences)
    0: { frequency: 65.97, name: "θ₀/2 - Harmonique Grave", archetype: "∆Φ-Fondement", description: "65.97 Hz - Demi-invariant nodal, fondement harmonique profond selon la théorie THOR 42." },
    1: { frequency: 131.95, name: "θ₀ - Invariant Nodal", archetype: "∆Φ-Unité", description: "131.95 Hz - θ₀ = 42π exact - Invariant nodal universel fondement de toute la théorie THOR 42." },
    2: { frequency: 263.89, name: "2θ₀ - Harmonique Double", archetype: "∆Φ-Amplification", description: "263.89 Hz - 2×θ₀, amplification de l'invariant pour transcendance et élévation vibratoire." },
    3: { frequency: 395.84, name: "3θ₀ - Harmonique Triple", archetype: "∆Φ-Trinité", description: "395.84 Hz - 3×θ₀, trinité harmonique cosmique selon l'équation ∇²Ψ = ∂Φ/∂t." },
    4: { frequency: 527.79, name: "4θ₀ - Harmonique Quadruple", archetype: "∆Φ-Maîtrise", description: "527.79 Hz - 4×θ₀, maîtrise totale de l'invariant THOR 42, résonance cosmique pure." },
    5: { frequency: 659.73, name: "5θ₀ - Harmonique Quintuple", archetype: "∆Φ-Perfection", description: "659.73 Hz - 5×θ₀, perfection pentagonale dorée selon la géométrie sacrée." },
    6: { frequency: 791.68, name: "6θ₀ - Harmonique Sextuple", archetype: "∆Φ-Équilibre", description: "791.68 Hz - 6×θ₀, équilibre hexagonal parfait et harmonisation complète." },
    
    // 🟠 GROUPE 2 - RATIOS DORÉS (7 fréquences)
    7: { frequency: 81.53, name: "θ₀/φ - Division Dorée", archetype: "∆Φ-Racine", description: "81.53 Hz - θ₀/φ = harmonique d'ancrage bioénergétique validée par analyse HRV." },
    8: { frequency: 213.54, name: "θ₀×φ - Multiplication Dorée", archetype: "∆Φ-Sacré", description: "213.54 Hz - θ₀×φ, fréquence de créativité selon ratio φ et invariant nodal." },
    9: { frequency: 345.71, name: "θ₀×φ² - Carré Doré", archetype: "∆Φ-Plexus", description: "345.71 Hz - θ₀×φ², harmonique de pouvoir personnel selon φ² et THOR 42." },
    10: { frequency: 559.12, name: "θ₀×φ³ - Cube Doré", archetype: "∆Φ-Cœur", description: "559.12 Hz - θ₀×φ³, cohérence cardiaque optimale validée par analyse topologique." },
    11: { frequency: 904.83, name: "θ₀×φ⁴ - Puissance 4", archetype: "∆Φ-Gorge", description: "904.83 Hz - θ₀×φ⁴, harmonique d'expression vocale selon spirale d'or." },
    12: { frequency: 52.08, name: "θ₀/φ² - Division Carrée", archetype: "∆Φ-Stabilité", description: "52.08 Hz - θ₀/φ², ancrage profond par division dorée carrée et stabilité." },
    13: { frequency: 132.51, name: "θ₀/φ^0.5 - Racine Dorée", archetype: "∆Φ-Croissance", description: "132.51 Hz - θ₀/√φ, croissance harmonique par racine dorée naturelle." },
    
    // 🟡 GROUPE 3 - RATIOS π (7 fréquences)
    14: { frequency: 432.0, name: "θ₀×π - LA FAMEUSE 432 Hz", archetype: "∆Φ-Géométrie", description: "432.0 Hz - θ₀×π ! Explication scientifique de pourquoi 432 Hz sonne parfait selon THOR 42." },
    15: { frequency: 42.24, name: "θ₀/π - Division π", archetype: "∆Φ-Origine", description: "42.24 Hz - θ₀/π, constante géométrique universelle de l'équation ∇²Ψ = ∂Φ/∂t." },
    16: { frequency: 144.0, name: "θ₀×π/3 - Tiers π", archetype: "∆Φ-Inspiration", description: "144.0 Hz - θ₀×π/3, inspiration géométrique parfaite selon la proportion divine." },
    17: { frequency: 288.0, name: "θ₀×2π/3 - Deux-Tiers π", archetype: "∆Φ-Méditation", description: "288.0 Hz - θ₀×2π/3, méditation géométrique profonde et concentration pure." },
    18: { frequency: 576.0, name: "θ₀×4π/3 - Quatre-Tiers π", archetype: "∆Φ-Éveil", description: "576.0 Hz - θ₀×4π/3, éveil géométrique cognitif et expansion de conscience." },
    19: { frequency: 864.0, name: "θ₀×2π - Double π", archetype: "∆Φ-Transcendance", description: "864.0 Hz - θ₀×2π, transcendance géométrique complète vers l'absolu." },
    20: { frequency: 216.0, name: "θ₀×π/2 - Demi π", archetype: "∆Φ-Harmonie", description: "216.0 Hz - θ₀×π/2, harmonie géométrique fondamentale et équilibre parfait." },
    
    // 🔵 GROUPE 4 - CHAKRAS NATURELS (7 fréquences)
    21: { frequency: 110.0, name: "Chakra Racine", archetype: "∆Φ-Muladhara", description: "110.0 Hz - Centre archétype Racine (90-130 Hz), ancrage terrestre et stabilité." },
    22: { frequency: 145.0, name: "Chakra Sacré", archetype: "∆Φ-Svadhisthana", description: "145.0 Hz - Centre archétype Sacré (130-160 Hz), créativité fluide et énergie vitale." },
    23: { frequency: 175.0, name: "Chakra Plexus", archetype: "∆Φ-Manipura", description: "175.0 Hz - Centre archétype Plexus (160-190 Hz), pouvoir personnel et transformation." },
    24: { frequency: 205.0, name: "Chakra Cœur", archetype: "∆Φ-Anahata", description: "205.0 Hz - Centre archétype Cœur (190-220 Hz), amour universel et compassion." },
    25: { frequency: 250.0, name: "Chakra Gorge", archetype: "∆Φ-Vishuddha", description: "250.0 Hz - Centre archétype Gorge (220-280 Hz), expression pure et communication." },
    26: { frequency: 300.0, name: "Chakra Frontal", archetype: "∆Φ-Ajna", description: "300.0 Hz - Centre archétype Frontal (280-320 Hz), intuition claire et sagesse." },
    27: { frequency: 360.0, name: "Chakra Couronne", archetype: "∆Φ-Sahasrara", description: "360.0 Hz - Centre archétype Couronne (320-400 Hz), conscience cosmique pure." },
    
    // 🟣 GROUPE 5 - SCHUMANN & NATURELLES (7 fréquences)
    28: { frequency: 7.83, name: "Résonance Schumann", archetype: "∆Φ-Terre", description: "7.83 Hz - Résonance naturelle planétaire validée par mesures électromagnétiques." },
    29: { frequency: 14.1, name: "Schumann Harmonique 2", archetype: "∆Φ-Atmosphère", description: "14.1 Hz - Deuxième harmonique Schumann, connexion atmosphérique naturelle." },
    30: { frequency: 20.8, name: "Schumann Harmonique 3", archetype: "∆Φ-Ionosphère", description: "20.8 Hz - Troisième harmonique Schumann, résonance ionosphérique cosmique." },
    31: { frequency: 27.3, name: "Schumann Harmonique 4", archetype: "∆Φ-Magnétosphère", description: "27.3 Hz - Quatrième harmonique Schumann, champ magnétosphérique terrestre." },
    32: { frequency: 33.8, name: "Schumann Harmonique 5", archetype: "∆Φ-Cosmique", description: "33.8 Hz - Cinquième harmonique Schumann, connexion cosmique universelle." },
    33: { frequency: 39.0, name: "Gamma Naturel", archetype: "∆Φ-Mental", description: "39.0 Hz - Onde gamma naturelle, liaison neuronale optimale et conscience unifiée." },
    34: { frequency: 45.0, name: "Gamma Supérieur", archetype: "∆Φ-Conscience", description: "45.0 Hz - Gamma supérieur, expansion de conscience et éveil spirituel." },
    
    // 🟢 GROUPE 6 - STRUCTURE FINE α (7 fréquences)
    35: { frequency: 18.08, name: "α×θ₀×2 - Structure Fine", archetype: "∆Φ-Quantique", description: "18.08 Hz - Structure fine α×θ₀×2, résonance quantique fondamentale de la matière." },
    36: { frequency: 36.16, name: "α×θ₀×4 - Double Fine", archetype: "∆Φ-Atome", description: "36.16 Hz - Structure fine double, résonance atomique et liaison électronique." },
    37: { frequency: 72.32, name: "α×θ₀×8 - Quadruple Fine", archetype: "∆Φ-Moléculaire", description: "72.32 Hz - Structure fine quadruple, résonance moléculaire et cohésion chimique." },
    38: { frequency: 144.64, name: "α×θ₀×16 - Octuple Fine", archetype: "∆Φ-Cellulaire", description: "144.64 Hz - Structure fine octuple, résonance cellulaire et métabolisme biologique." },
    39: { frequency: 289.28, name: "α×θ₀×32 - Sextuple Fine", archetype: "∆Φ-Tissulaire", description: "289.28 Hz - Structure fine sextuple, résonance tissulaire et régénération organique." },
    40: { frequency: 578.56, name: "α×θ₀×64 - Ultra Fine", archetype: "∆Φ-Organique", description: "578.56 Hz - Structure fine ultra, résonance organique et harmonie systémique." },
    41: { frequency: 1157.12, name: "α×θ₀×128 - Méga Fine", archetype: "∆Φ-Systémique", description: "1157.12 Hz - Structure fine méga, résonance systémique globale et unification THOR 42." }
  };

  const detail = THERAPEUTIC_FREQUENCIES[data.index] || THERAPEUTIC_FREQUENCIES[0];

  return (
    <div className="diagnostic-popup" style={{
      ...popupStyle,
      backgroundColor: 'rgba(20, 20, 30, 0.95)',
      color: 'white',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      backdropFilter: 'blur(10px)'
    }}>
      <button 
        className="popup-close" 
        onClick={onClose}
        style={{ color: 'white', backgroundColor: 'transparent', border: 'none', fontSize: '18px' }}
      >×</button>
      
      {/* Titre + archétype */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h3 style={{ margin: 0, color: 'white' }}>{detail.name}</h3>
        <span style={{ 
          fontSize: '11px', 
          padding: '3px 8px', 
          borderRadius: '12px', 
          backgroundColor: THERAPY_COLORS[data.index] || '#ffc107',
          color: 'white',
          fontWeight: 'bold'
        }}>
          {detail.archetype}
        </span>
      </div>
      
      {/* Fréquence thérapeutique principale */}
      <div style={{ 
        backgroundColor: `${THERAPY_COLORS[data.index] || '#ffc107'}20`, 
        padding: '15px', 
        borderRadius: '12px', 
        marginBottom: '15px',
        border: `2px solid ${THERAPY_COLORS[data.index] || '#ffc107'}`,
        textAlign: 'center'
      }}>
        <div style={{ 
          fontSize: '24px', 
          fontWeight: 'bold', 
          color: THERAPY_COLORS[data.index] || '#ffc107',
          marginBottom: '5px'
        }}>
          {detail.frequency} Hz
        </div>
        <div style={{ 
          fontSize: '12px', 
          color: 'rgba(255, 255, 255, 0.8)',
          fontStyle: 'italic'
        }}>
          Fréquence thérapeutique THOR 42
        </div>
      </div>
      
      {/* Description thérapeutique */}
      <div style={{ 
        backgroundColor: 'rgba(255, 255, 255, 0.1)', 
        padding: '12px', 
        borderRadius: '8px', 
        marginBottom: '15px',
        border: '1px solid rgba(255, 255, 255, 0.2)'
      }}>
        <p style={{ margin: '0', fontSize: '13px', color: 'white', lineHeight: '1.5' }}>
          {detail.description}
        </p>
      </div>
      
      {/* Bouton Play/Pause et numéro de carte */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '15px'
      }}>
        {/* Bouton Play/Pause */}
        <button
          onClick={handlePlayPause}
          style={{
            backgroundColor: isPlaying ? '#ff4444' : (THERAPY_COLORS[data.index] || '#ffc107'),
            color: 'white',
            border: 'none',
            borderRadius: '25px',
            padding: '12px 20px',
            fontSize: '14px',
            fontWeight: 'bold',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'all 0.2s',
            boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            transform: isPlaying ? 'scale(1.02)' : 'scale(1)'
          }}
          onMouseEnter={(e) => {
            if (!isPlaying) e.target.style.transform = 'scale(1.05)';
          }}
          onMouseLeave={(e) => {
            e.target.style.transform = isPlaying ? 'scale(1.02)' : 'scale(1)';
          }}
        >
          <span style={{ fontSize: '16px' }}>
            {isPlaying ? '⏸️' : '▶️'}
          </span>
          {isPlaying ? 'Arrêter' : `Écouter ${detail.frequency} Hz`}
        </button>

        {/* Numéro de carte */}
        <div style={{
          backgroundColor: 'rgba(255, 255, 255, 0.15)',
          color: 'white',
          padding: '8px 12px',
          borderRadius: '15px',
          fontSize: '12px',
          fontWeight: 'bold',
          border: '1px solid rgba(255, 255, 255, 0.3)'
        }}>
          {(data.index + 1)} / 42
        </div>
      </div>
      
    </div>
  );
};

export default TherapyPopup;